import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ScrollText,
  Search,
  ShieldAlert,
  Target,
  X,
} from "lucide-react";
import { useAdminAuditLogs } from "../../hooks/useAdmin";
import { useFilterParams } from "../../hooks/useFilterParams";
import { Pagination } from "./Pagination";

const PAGE_SIZE = 25;

const FILTER_DEFAULTS = {
  actor_user_id: "",
  target_type: "",
  target_id: "",
  offset: 0,
};

/** The backend matches ids exactly, so a partial string silently matches all. */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const TARGET_FILTERS = [
  { label: "All targets", value: "" },
  { label: "Users", value: "user" },
  { label: "Routing rules", value: "routing_rule" },
  { label: "Provider capabilities", value: "provider_capability" },
];

/**
 * Append-only trail of privileged admin actions.
 *
 * Deliberately read-only: there is no edit or delete affordance, because an
 * audit record that can be changed is not an audit record. The backend
 * repository exposes no mutation methods either.
 */
export function AdminAudit() {
  // Read from the URL so other pages can deep-link into a filtered trail -
  // "show me everything done to this account" is the question this page most
  // often has to answer, and it arrives from the user detail panel.
  const { values, setValues, setFilters, clearFilters, isFiltered } =
    useFilterParams(FILTER_DEFAULTS);
  const {
    actor_user_id: actorUserId,
    target_type: targetType,
    target_id: targetId,
    offset,
  } = values;

  const [searchInput, setSearchInput] = useState(actorUserId);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setSearchInput((current) => (current === actorUserId ? current : actorUserId));
  }, [actorUserId]);

  const typedActor = searchInput.trim();
  // A partial id is not a partial match - the backend compares exactly, so a
  // half-typed or mistyped id is dropped and the page shows everything, which
  // reads as "the filter is broken". Only send a well-formed id, and say so.
  const actorIdInvalid = typedActor.length > 0 && !UUID_PATTERN.test(typedActor);

  useEffect(() => {
    const next = UUID_PATTERN.test(typedActor) ? typedActor : "";
    if (next === actorUserId) return undefined;
    const timer = setTimeout(
      () => setValues({ actor_user_id: next, offset: 0 }, { replace: true }),
      300
    );
    return () => clearTimeout(timer);
  }, [typedActor, actorUserId, setValues]);

  const params = useMemo(
    () => ({ limit: PAGE_SIZE, offset, targetType, targetId, actorUserId }),
    [offset, targetType, targetId, actorUserId]
  );

  const { entries, total, isLoading, error } = useAdminAuditLogs(params);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
          Accountability
        </h2>
        <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
          Admin Audit Log
        </h1>
        <p className="text-sm text-[#0A2540]/50 mt-2 max-w-2xl">
          Every privileged action - account suspensions, compliance decisions,
          role grants, and routing changes - with the staff member who took it
          and the reason they gave. Records are append-only.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          Could not load the audit log. It requires the <code>audit:read</code>{" "}
          permission.
        </div>
      )}

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              aria-label="Filter by acting staff user ID"
              aria-invalid={actorIdInvalid}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Filter by acting staff user ID…"
              className={`w-full rounded-xl border pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-4 focus:ring-[#0A4730]/5 ${
                actorIdInvalid
                  ? "border-amber-300 focus:border-amber-400"
                  : "border-slate-200 focus:border-[#0A4730]"
              }`}
            />
            {actorIdInvalid && (
              <p className="text-xs text-amber-600 mt-1.5">
                Enter a complete user ID. This field matches exactly, so a
                partial ID is ignored rather than searched.
              </p>
            )}
          </div>
          <select
            aria-label="Filter by target type"
            value={targetType}
            onChange={(event) => setFilters({ target_type: event.target.value })}
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-[#0A4730]"
          >
            {TARGET_FILTERS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {isFiltered && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs font-semibold text-[#0A4730] hover:bg-slate-50 transition"
            >
              <X size={14} /> Clear filters
            </button>
          )}
        </div>

        {/* A deep link from another page pins the trail to one record. Say so
            plainly and make it removable - otherwise the page looks like it is
            missing most of the log. */}
        {targetId && (
          <div className="flex items-center gap-2 rounded-2xl bg-[#F5F9F4] border border-[#C0F6B7]/60 px-4 py-3">
            <Target size={15} className="text-[#0A4730] shrink-0" />
            <p className="text-xs text-[#0A4730] min-w-0">
              Showing only actions against{" "}
              <span className="font-semibold">{targetType || "target"}</span>{" "}
              <span className="font-mono break-all">{targetId}</span>
            </p>
            <button
              type="button"
              onClick={() => setFilters({ target_id: "", target_type: "" })}
              className="ml-auto shrink-0 text-xs font-semibold text-[#0A4730] hover:underline underline-offset-4"
            >
              Show all
            </button>
          </div>
        )}

        <div className="space-y-2">
          {isLoading ? (
            <p className="py-10 text-center text-sm text-[#0A2540]/50">
              Loading audit entries…
            </p>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#0A2540]/50">
              {isFiltered ? (
                <>
                  <p>No admin actions match these filters.</p>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-2 text-xs font-semibold text-[#0A4730] underline underline-offset-4"
                  >
                    Clear filters
                  </button>
                </>
              ) : (
                <p>No admin actions recorded yet.</p>
              )}
            </div>
          ) : (
            entries.map((entry) => (
              <AuditRow
                key={entry.id}
                entry={entry}
                expanded={expandedId === entry.id}
                onToggle={() =>
                  setExpandedId(expandedId === entry.id ? null : entry.id)
                }
              />
            ))
          )}
        </div>

        <Pagination
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          onChange={(next) => setValues({ offset: next })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function AuditRow({ entry, expanded, onToggle }) {
  const destructive = isDestructive(entry.action);

  return (
    <div className="rounded-2xl border border-slate-100 bg-[#FDFDFD] overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 p-4 text-left hover:bg-[#F5F9F4]/60 transition"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${
              destructive
                ? "bg-red-50 text-red-500"
                : "bg-[#C0F6B7]/40 text-[#0A4730]"
            }`}
          >
            {destructive ? <ShieldAlert size={16} /> : <ScrollText size={16} />}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0A2540] break-words">
              {humanizeAction(entry.action)}
              {/* Naming the subject on the collapsed row is the difference
                  between a scannable trail and a column of UUIDs. */}
              {entry.target_label && (
                <span className="font-normal text-[#0A2540]/60">
                  {" · "}
                  {entry.target_label}
                </span>
              )}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {entry.actor_name || entry.actor_email || entry.actor_user_id}
              <span className="mx-1.5">·</span>
              <span className="capitalize">{entry.actor_role}</span>
              <span className="mx-1.5">·</span>
              {formatDate(entry.created_at)}
            </p>
            {entry.reason && (
              <p className="text-xs text-[#0A2540]/70 mt-1.5 italic break-words">
                “{entry.reason}”
              </p>
            )}
          </div>
        </div>
        <div className="shrink-0 text-slate-400">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-2 text-xs">
          <DetailLine
            label="Target"
            value={
              entry.target_label
                ? `${entry.target_label} · ${entry.target_type} ${entry.target_id || ""}`
                : `${entry.target_type} ${entry.target_id || ""}`
            }
          />
          <DetailLine label="Actor ID" value={entry.actor_user_id} />
          {entry.context && (
            <div>
              <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-1">
                Context
              </p>
              <pre className="bg-white border border-slate-100 rounded-xl p-3 overflow-x-auto text-[11px] text-[#0A2540]">
                {JSON.stringify(entry.context, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailLine({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-[9px] uppercase tracking-widest text-slate-400 pt-0.5 w-20 shrink-0">
        {label}
      </span>
      <span className="font-mono text-[11px] text-[#0A2540] break-all">{value}</span>
    </div>
  );
}

function isDestructive(action = "") {
  return (
    action.includes("suspended") ||
    action.includes("closed") ||
    action.includes("blocked") ||
    action.includes("delete") ||
    action.includes("failed")
  );
}

function humanizeAction(action = "") {
  return action.replaceAll(".", " › ").replaceAll("_", " ");
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}
